/*
 * ═══════════════════════════════════════════════════════════════════════════
 * ARCHITECTURE RULE: Transport commands (pause, play, stop, playUri)
 * may ONLY be called from:
 *
 *   1. QueueController — track advance, _playTrack
 *   2. Direct user gesture handlers — button onClick, lock screen controls
 *   3. authorizeIosPause() gate — iOS media session only
 *
 * NEVER call transport commands from:
 *   - Zustand subscribers
 *   - useEffect hooks watching zones or renderers
 *   - BATON event handlers
 *   - Discovery poll callbacks
 *
 * This rule exists because a Zustand subscriber calling pause() caused
 * a full RAAT cascade failure at Axpona 2026.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useMaestroStore } from '../store/maestroStore';
import { isLimitedRenderer } from './types';
import { getSyncHandler } from '../handlers/ClientSyncHandler';
import { detectPlatform } from '../utils/platform';
import { diagnosticLog } from '../utils/diagnosticLogger';

/** @deprecated No-op — iOS pause gate removed. User-initiated pauses are gated by userInitiated flag. */
export function authorizeIosPause(): void {
  // Kept as a no-op for callers that haven't been cleaned up yet.
}

/** Minimum gap between consecutive RAAT/native transport dispatches (ms).
 *  PMG and other Roon Ready devices drop commands that arrive <500ms apart. */
const RAAT_MIN_GAP_MS = 500;
let _lastRaatCommandAt = 0;

async function _enforceRaatGap(): Promise<void> {
  const elapsed = Date.now() - _lastRaatCommandAt;
  if (elapsed < RAAT_MIN_GAP_MS) {
    await new Promise(r => setTimeout(r, RAAT_MIN_GAP_MS - elapsed));
  }
  _lastRaatCommandAt = Date.now();
}

type ConnectorModule = typeof import('../lib/connector');

type PendingCommand = {
  type: 'play' | 'pause' | 'playUri';
  execute: () => Promise<void>;
  resolve: () => void;
  reject: (err: any) => void;
};

class TransportCommandImpl {
  private _busy = false;
  private _queue: PendingCommand[] = [];
  private _audioRef: HTMLAudioElement | null = null;
  private _commandTimeout = 3000;

  private _volumeEcho: { target: number; timestamp: number } | null = null;
  private _volumeEchoWindow = 3000;
  private _volumeDragUntil = 0;

  private _playUriFails = 0;
  private _playUriLastFail = 0;
  private _lastPlayUriTimestamp = 0;
  /** Set when playUri native/connector dispatch succeeds — used to ignore stale sync pauses. */
  private _lastPlayUriSuccessAt = 0;
  private _volumeDebounceTimer: number | null = null;

  private _connector: ConnectorModule | null = null;
  private _iosCheckLogged = false;

  /** iOS playUri confirmation generation — stale callbacks ignore old waits */
  private _iosPlayConfirmGen = 0;
  /** Fix D: delayed ghost self-heal */
  private _ghostPlaybackTimer: ReturnType<typeof setTimeout> | null = null;

  /** Exclusive audio engine state poller interval */
  private _exclusivePoller: ReturnType<typeof setInterval> | null = null;
  /** Track whether we're in exclusive native playback mode */
  private _exclusiveActive = false;

  private async _getConnector(): Promise<ConnectorModule> {
    if (!this._connector) {
      this._connector = await import('../lib/connector');
    }
    return this._connector;
  }

  /** Check if we should route audio through the native exclusive engine (bit-perfect USB DAC path). */
  private async _shouldUseExclusive(): Promise<boolean> {
    if (!this._isTauri()) return false;
    const zone = useMaestroStore.getState().discovery?.activeZone;
    const zoneType = (zone as any)?.type;
    const zoneId = (zone as any)?.id;
    // Local-native zones (Rust-enumerated USB DACs) always use exclusive engine
    if (zoneType === 'local-native') return true;
    // If the zone is a network renderer (not local at all), never use exclusive
    if (zoneType && zoneType !== 'local' && zoneType !== 'browser') return false;
    // If the zone is explicitly "System Audio" (not a USB DAC), skip exclusive
    if (zoneId === 'this_device' || zoneId === 'local_computer' || zoneId === 'this_computer') return false;
    // Legacy selectedUsbDac path (for zones selected via the old auto-detect)
    const { selectedUsbDac } = useMaestroStore.getState();
    if (!selectedUsbDac?.uid) return false;
    try {
      const { isExclusiveModeEnabled } = await import('../utils/usbDacNames');
      return isExclusiveModeEnabled(selectedUsbDac.uid);
    } catch {
      return false;
    }
  }

  /** Start polling the exclusive engine for position/state updates */
  private _startExclusivePoller() {
    this._stopExclusivePoller();
    this._exclusiveActive = true;
    this._exclusivePoller = setInterval(async () => {
      try {
        const C = await this._getConnector();
        const state = await C.exclusiveGetState();
        const store = useMaestroStore.getState();
        if (state.is_playing) {
          store.updateTime(state.position_secs, state.duration_secs);
        }
        if (state.duration_secs > 0 && state.position_secs >= state.duration_secs - 0.3 && !state.is_buffering) {
          console.log('[TCmd] Exclusive track ended — advancing');
          this._stopExclusivePoller();
          const { QueueController: QC } = await import('./QueueController');
          await QC.advanceTrack('next');
        }
      } catch (err) {
        console.warn('[TCmd] Exclusive poller error:', err);
      }
    }, 500);
  }

  private _stopExclusivePoller() {
    if (this._exclusivePoller) {
      clearInterval(this._exclusivePoller);
      this._exclusivePoller = null;
    }
    this._exclusiveActive = false;
  }

  registerAudioElement(el: HTMLAudioElement) {
    this._audioRef = el;
  }

  unregisterAudioElement() {
    this._audioRef = null;
  }

  /**
   * Defensive helper: pause the browser's local HTML5 audio element
   * if it's currently playing. Used when switching playback surfaces
   * (e.g., radio on "This Computer" → library on a network renderer) to
   * prevent the old stream from continuing alongside the new one.
   *
   * We only pause — we do NOT clear the src. If the subsequent dispatch
   * routes back to local audio, WebAudioRenderer will reactively set a
   * new src via its trackChanged handler and playback resumes cleanly.
   * Safe to call at any time; no-op when no audio is active.
   */
  pauseLocalAudioElement(): void {
    const el = this._audioRef;
    if (!el) return;
    try {
      if (!el.paused) {
        console.log('[TransportCommand] pauseLocalAudioElement — halting stale local stream');
        el.pause();
      }
    } catch (e) {
      console.warn('[TransportCommand] pauseLocalAudioElement failed:', e);
    }
  }

  private _isMultizoneActive(): boolean {
    const mz = useMaestroStore.getState().multizone;
    return mz.enabled && mz.groupedZones.length >= 2;
  }

  private async _multizonePlayAll(): Promise<void> {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;
    if (!this._isTauri()) return;
    const C = await this._getConnector();

    const streamUrl = store.currentStreamUrl;
    const formatId = store.currentStreamFormatId || 6;
    const track = store.currentTrack;

    // Route resume through the Connector's sync engine so it re-sends
    // SetAVTransportURI (needed for WiiM which loses its URI on Stop-for-Pause),
    // applies stagger timing, and spawns a fresh drift monitor.
    if (streamUrl && track) {
      console.log('[TransportCommand] multizonePlayAll — routing through multizone_play for staggered resume');
      try {
        await C.multizonePlay(
          zones.map(z => ({ renderer_id: z.zoneId, volume: z.actualVolume ?? null })),
          streamUrl,
          track?.title || '', track?.artist || '', track?.album || '',
          formatId,
          Math.round(track?.duration || 0),
          track?.artworkUrl || track?.artUrl,
        );
        store.setRendererHasStream(true);
        return;
      } catch (err: any) {
        console.warn('[TransportCommand] multizone_play IPC failed, falling back to per-device play:', err?.message || err);
      }
    }

    // Fallback: per-device play (no stagger, no drift monitor)
    const results = await Promise.allSettled(
      zones.map(z => C.play(z.zoneId).catch(err => {
        console.error(`[TransportCommand] multizone play failed for ${z.zoneName}:`, err);
        throw err;
      }))
    );
    console.log('[TransportCommand] multizonePlayAll fallback results:', results.map(r => r.status));
  }

  private async _multizonePauseAll(): Promise<void> {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;
    if (!this._isTauri()) return;
    const C = await this._getConnector();

    // Cancel the drift monitor FIRST — prevents wild seek corrections on paused devices
    try {
      await C.multizonePause();
    } catch (err: any) {
      console.warn('[TransportCommand] multizonePause IPC failed (drift monitor may persist):', err?.message || err);
    }

    // Then send per-device pause commands
    await Promise.allSettled(
      zones.map(z => C.pause(z.zoneId).catch(err => {
        console.error(`[TransportCommand] multizone pause failed for ${z.zoneName}:`, err);
      }))
    );
  }

  private async _multizoneStopAll(): Promise<void> {
    if (!this._isTauri()) return;
    const C = await this._getConnector();

    // Connector's multizone_stop cancels the drift monitor AND stops all renderers
    try {
      await C.multizoneStop();
    } catch (err: any) {
      console.warn('[TransportCommand] multizoneStop IPC failed, falling back to per-device stop:', err?.message || err);
      const zones = useMaestroStore.getState().multizone.groupedZones;
      await Promise.allSettled(
        zones.map(z => C.stop(z.zoneId).catch(stopErr => {
          console.error(`[TransportCommand] multizone stop failed for ${z.zoneName}:`, stopErr);
        }))
      );
    }
  }

  private async _multizoneSeekAll(positionSeconds: number): Promise<void> {
    const store = useMaestroStore.getState();
    const zones = store.multizone.groupedZones;
    if (!this._isTauri()) return;
    const C = await this._getConnector();
    const secs = Math.floor(positionSeconds);
    await Promise.allSettled(
      zones.map(z => C.seek(z.zoneId, secs).catch(err => {
        console.error(`[TransportCommand] multizone seek failed for ${z.zoneName}:`, err);
      }))
    );
  }

  /** If UI has a network activeZone but transport lost activeConnectorRenderer, re-hydrate.
   *  Also clears stale renderers from BATON sync that aren't locally discovered. */
  private _hydrateConnectorRendererFromActiveZone(): void {
    const s = useMaestroStore.getState();
    const zones = s.discovery?.discoveredZones || [];
    const hasBridge = this._isTauri() || this._isIOS();

    // Clear stale renderer from BATON sync — prevents routing to unreachable devices.
    // Also clear when the platform has no bridge (browser-only) — a synced renderer
    // from another device is unreachable and blocks local audio pause/play.
    if (s.activeConnectorRenderer) {
      if (!hasBridge) {
        console.log('[TCmd] Clearing synced renderer on bridgeless platform:', s.activeConnectorRenderer.id);
        s.setActiveConnectorRenderer(null);
        return;
      }
      if (zones.length > 0 && !zones.some((z: any) => z.id === s.activeConnectorRenderer!.id)) {
        console.log('[TCmd] Clearing stale renderer (not in local zones):', s.activeConnectorRenderer.id);
        s.setActiveConnectorRenderer(null);
        const az = s.discovery?.activeZone as any;
        if (az && az.type !== 'local' && az.type !== 'browser' && az.type !== 'local-native'
            && az.id !== 'this_computer' && az.id !== 'local_computer' && az.id !== 'this_device') {
          s.setActiveZone({ id: 'this_device', name: 'System Audio', type: 'local' } as any);
        }
      } else {
        return; // Renderer valid and present
      }
    }

    const az = s.discovery?.activeZone as { id?: string; name?: string; type?: string } | undefined;
    if (!az?.id) return;
    if (az.type === 'local' || az.type === 'browser'
        || az.id === 'this_computer' || az.id === 'local_computer' || az.id === 'this_device') {
      return;
    }
    // Never hydrate a renderer on a platform that has no bridge to reach it
    if (!hasBridge) return;
    // Only hydrate if the zone is locally discovered
    if (zones.length > 0 && !zones.some((z: any) => z.id === az.id)) {
      console.log('[TCmd] _hydrate skipped: zone not in local discoveredZones', az.id);
      return;
    }
    s.setActiveConnectorRenderer({ id: az.id, name: az.name || az.id });
  }

  /**
   * RESUME ONLY — never initiates new playback, never calls QueueController.
   *
   * play() resumes an existing stream. If there is no currentStreamUrl,
   * play() is a no-op. New playback is ONLY initiated by QueueController
   * (playAll, playFrom, playQueueTrack, next, previous) which calls
   * TransportCommand.playUri() with an already-resolved URI.
   */
  async play() {
    this._hydrateConnectorRendererFromActiveZone();
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;
    const url = store.currentStreamUrl;

    if (!url) {
      console.log('[Transport] play() — no active URI, no-op. Select a track to start playback.');
      return;
    }

    // Multizone resume
    if (this._isMultizoneActive() && store.rendererHasStream) {
      store.setPlaybackState('playing');
      return this._enqueue('play', async () => {
        try {
          await this._multizonePlayAll();
        } catch (err: any) {
          console.error('[TransportCommand.play] multizone resume failed:', err?.message || err);
          useMaestroStore.getState().setPlaybackState('paused');
        }
      });
    }

    // Renderer present but stream not yet sent — re-send the cached URL
    if (renderer && !store.rendererHasStream) {
      const formatId = store.currentStreamFormatId || 27;
      console.log('[Transport] play() — renderer has no stream, re-sending cached URI to', renderer.name);
      return this.playUri(url, renderer.id, formatId, store.currentTrack);
    }

    // Exclusive engine resume
    if (this._exclusiveActive) {
      store.setPlaybackState('playing');
      return this._enqueue('play', async () => {
        try {
          const C = await this._getConnector();
          await C.exclusiveResume();
          this._startExclusivePoller();
        } catch (err: any) {
          console.error('[TCmd] exclusive resume failed:', err?.message || err);
          useMaestroStore.getState().setPlaybackState('paused');
        }
      });
    }

    // iOS native without a network renderer
    if (!renderer && !this._audioRef && this._isIOS()) {
      store.setPlaybackState('playing');
      this._iosPostMessage('play', {});
      return;
    }

    if (!renderer && !this._audioRef) {
      console.log('[Transport] play() — no renderer, no audioRef, no-op');
      return;
    }

    // Standard resume: send play to renderer and/or local audio element
    store.setPlaybackState('playing');

    return this._enqueue('play', async () => {
      const s = useMaestroStore.getState();
      const r = s.activeConnectorRenderer;
      const hasBridge = this._isTauri() || this._isIOS();

      try {
        if (r && this._isTauri()) {
          const C = await this._getConnector();
          await C.play(r.id);
        } else if (r && this._isIOS()) {
          this._iosSendCommand(r.id, 'play');
        }

        if (this._audioRef && this._audioRef.paused && this._audioRef.currentSrc) {
          if (!r || !hasBridge) {
            await this._audioRef.play();
          }
        }
      } catch (err: any) {
        console.error('[Transport] play() resume failed:', err?.message || err);
        useMaestroStore.getState().setPlaybackState('paused');
      }
    });
  }

  /** @param userInitiated - must be true for tap / lock-screen / media-session pause so the Axpona guard does not block real user stops */
  async pause(userInitiated = false) {
    // Observer devices must NEVER send transport commands to the renderer —
    // the controller owns the renderer. Observers only update the local store.
    if (!userInitiated) {
      try {
        if (getSyncHandler().isObserving()) {
          console.warn('[TCmd] pause BLOCKED — device is observing (controller owns renderer)');
          return;
        }
      } catch {}
    }
    // AXPONA EMERGENCY GUARD — block spurious pauses while renderer has a stream and UI says playing
    if (!userInitiated) {
      const { rendererHasStream, playbackState } = useMaestroStore.getState();
      if (rendererHasStream && playbackState === 'playing') {
        console.warn('[TCmd] pause BLOCKED — rendererHasStream=true and state=playing. Stack:', new Error().stack);
        return;
      }
    }
    // Suppress spurious auto-pauses that fire right after playUri succeeds.
    // User-initiated pauses (button taps, lock screen, media session) MUST
    // always go through — never block an explicit user action.
    if (!userInitiated && this._lastPlayUriSuccessAt > 0 && Date.now() - this._lastPlayUriSuccessAt < 500) {
      console.log('[TCmd] pause BLOCKED — too soon after playUri (non-user-initiated)');
      return;
    }

    this._hydrateConnectorRendererFromActiveZone();
    const renderer = useMaestroStore.getState().activeConnectorRenderer;
    console.log('[TransportCommand.pause] renderer:', renderer?.name, renderer?.id,
      'isTauri:', this._isTauri(), 'isIOS:', this._isIOS(),
      'multizone:', this._isMultizoneActive());

    // Multizone pause: send pause to all zones in parallel
    if (this._isMultizoneActive()) {
      useMaestroStore.getState().setPlaybackState('paused');
      return this._enqueue('pause', async () => {
        try {
          await this._multizonePauseAll();
        } catch (err: any) {
          console.error('[TransportCommand.pause] multizone pause failed:', err?.message || err);
        }
      });
    }

    // Exclusive engine pause
    if (this._exclusiveActive) {
      console.log('[TCmd] pause → exclusive engine pause');
      useMaestroStore.getState().setPlaybackState('paused');
      this._stopExclusivePoller();
      return this._enqueue('pause', async () => {
        try {
          const C = await this._getConnector();
          await C.exclusivePause();
        } catch (err: any) {
          console.error('[TCmd] exclusive pause failed:', err?.message || err);
        }
      });
    }

    // iOS native without a network renderer: send pause through the native bridge
    // for local AVPlayer playback. WebAudioRenderer is not mounted on iOS.
    if (!renderer && !this._audioRef && this._isIOS()) {
      console.log('[TCmd] pause → iOS local playback (native bridge)');
      useMaestroStore.getState().setPlaybackState('paused');
      this._iosPostMessage('pause', {});
      return;
    }

    if (!renderer && !this._audioRef) {
      console.error('[TransportCommand.pause] NO RENDERER and no audioRef — nowhere to send command');
      useMaestroStore.getState().setPlaybackState('paused');
      return;
    }

    useMaestroStore.getState().setPlaybackState('paused');

    return this._enqueue('pause', async () => {
      const store = useMaestroStore.getState();
      const renderer = store.activeConnectorRenderer;
      const hasBridge = this._isTauri() || this._isIOS();
      const az = store.discovery?.activeZone as any;

      try {
        if (renderer && this._isTauri()) {
          const C = await this._getConnector();
          await C.pause(renderer.id);
        } else if (renderer && this._isIOS()) {
          this._iosSendCommand(renderer.id, 'pause', true);
        }

        if (this._audioRef && !this._audioRef.paused) {
          this._audioRef.pause();
        }
      } catch (err: any) {
        console.error('[TransportCommand.pause] failed (keeping paused — device may be unreachable):', err?.message || err);
        if (this._audioRef && !this._audioRef.paused) {
          try { this._audioRef.pause(); } catch {}
        }
      }
    });
  }

  async seek(positionSeconds: number) {
    this._hydrateConnectorRendererFromActiveZone();
    const store = useMaestroStore.getState();
    const r = store.activeConnectorRenderer;
    const az = store.discovery?.activeZone as any;
    diagnosticLog(
      'info',
      'transport',
      `Command dispatched: seek to ${Math.floor(positionSeconds)}s`,
      {
        command: 'seek',
        target: r?.name || az?.name || 'local',
        rendererType: az?.transport_type || az?.type,
      },
    );
    store.updateTime(positionSeconds, store.duration);

    // Multizone seek: send to all zones in parallel
    if (this._isMultizoneActive()) {
      await this._multizoneSeekAll(positionSeconds);
      return;
    }

    const renderer = store.activeConnectorRenderer;
    const secs = Math.floor(positionSeconds);

    if (renderer && this._isTauri()) {
      const C = await this._getConnector();
      await C.seek(renderer.id, secs);
    } else if (renderer && this._isIOS()) {
      this._iosPostMessage('seek', { deviceId: renderer.id, seconds: secs });
    } else if (this._audioRef) {
      this._audioRef.currentTime = positionSeconds;
    } else {
      console.error('[TransportCommand.seek] NO RENDERER and no audioRef — nowhere to send command');
    }
  }

  async volume(level: number) {
    this._hydrateConnectorRendererFromActiveZone();
    const store = useMaestroStore.getState();
    const renderer = store.activeConnectorRenderer;

    if (!renderer && !this._audioRef) {
      return;
    }

    this._volumeEcho = { target: level, timestamp: Date.now() };
    store.setVolume(level);
    const az = store.discovery?.activeZone as any;
    diagnosticLog(
      'info',
      'volume',
      `Volume command: ${Math.round(level)} to ${renderer?.name || az?.name || 'local'}`,
      {
        level: Math.round(level),
        renderer: renderer?.name,
        rendererType: az?.transport_type || az?.type,
        muted: !!store.muted,
      },
    );

    if (renderer && this._isTauri()) {
      const C = await this._getConnector();
      await C.setVolume(renderer.id, Math.round(level));
    } else if (renderer && this._isIOS()) {
      this._iosPostMessage('setVolume', {
        deviceId: renderer.id,
        level: Math.round(level),
      });
    } else if (this._audioRef) {
      this._audioRef.volume = level / 100;
    }
  }

  isPlayUriRecent(withinMs = 8000): boolean {
    return this._lastPlayUriTimestamp > 0 && (Date.now() - this._lastPlayUriTimestamp) < withinMs;
  }

  /** True for 2s after playUri success — suppress stale BATON/sync/renderer paused state. */
  isInPlayUriPostSuccessSilenceWindow(): boolean {
    return this._lastPlayUriSuccessAt > 0 && Date.now() - this._lastPlayUriSuccessAt < 2000;
  }

  startVolumeDrag(): void {
    this._volumeDragUntil = Infinity;
  }

  endVolumeDrag(): void {
    this._volumeDragUntil = Date.now() + 3000;
  }

  isVolumeEchoSuppressed(reportedLevel: number): boolean {
    if (Date.now() < this._volumeDragUntil) return true;
    if (!this._volumeEcho) return false;
    const elapsed = Date.now() - this._volumeEcho.timestamp;
    if (elapsed > this._volumeEchoWindow) {
      this._volumeEcho = null;
      return false;
    }
    return reportedLevel !== this._volumeEcho.target;
  }

  private _logPlayUriUrl(url: string): string {
    if (!url) return '(empty)';
    return url.length > 140 ? `${url.slice(0, 140)}…` : url;
  }

  /**
   * iOS: playUri is fire-and-forget — wait for native transport PLAYING or store position > 0.
   */
  private _waitForIOSPlaybackEvidence(rendererId: string, timeoutMs: number, generation: number): Promise<boolean> {
    return new Promise((resolve) => {
      let settled = false;
      const onTransport = (ev: Event) => {
        const d = (ev as CustomEvent).detail || {};
        if (generation !== this._iosPlayConfirmGen) { finish(false); return; }
        if (d.deviceId !== rendererId) return;
        if (d.state === 'PLAYING') finish(true);
      };
      const unsub = useMaestroStore.subscribe((state) => {
        if (generation !== this._iosPlayConfirmGen) { finish(false); return; }
        if (state.activeConnectorRenderer?.id !== rendererId) return;
        if ((state.position || 0) > 0) finish(true);
      });
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        try { window.removeEventListener('maestroTransportState', onTransport as EventListener); } catch { /* ignore */ }
        try { unsub(); } catch { /* ignore */ }
        try { clearTimeout(timer); } catch { /* ignore */ }
        resolve(ok);
      };
      const timer = setTimeout(() => finish(false), timeoutMs);
      window.addEventListener('maestroTransportState', onTransport as EventListener);
    });
  }

  private _clearGhostPlaybackTimer(): void {
    if (this._ghostPlaybackTimer) {
      clearTimeout(this._ghostPlaybackTimer);
      this._ghostPlaybackTimer = null;
    }
  }

  private _iosDispatchPlayUriPayload(
    uri: string,
    rendererId: string,
    formatId: number,
    track: any,
  ): void {
    this._iosPostMessage('playUri', {
      rendererId,
      uri,
      formatId,
      title: track?.title || '',
      artist: track?.artist || '',
      album: track?.album || '',
      durationSecs: Math.round(track?.duration || 0),
      artUrl: track?.artworkUrl || track?.artUrl || '',
    });
  }

  /**
   * Runs AFTER the playUri mutex releases — never block pause/volume/seek.
   * May post playUri again once if no PLAYING/position evidence (Fix B).
   */
  private _iosBackgroundConfirmPlayback(
    uri: string,
    rendererId: string,
    formatId: number,
    track: any,
    generation: number,
    options?: { skipGhostWatchdog?: boolean },
  ): void {
    void (async () => {
      let ok = await this._waitForIOSPlaybackEvidence(rendererId, 3000, generation);
      if (!ok) {
        // Bail if a newer playUri has already been dispatched (track changed)
        if (generation !== this._iosPlayConfirmGen) return;
        // Also check the store's current track hasn't changed
        const curId = useMaestroStore.getState().currentTrack?.id;
        const origId = track?.id;
        if (origId != null && curId != null && String(curId) !== String(origId)) return;

        console.warn('[TCmd] playUri watchdog: no playback confirmed after 3s — retrying (background)');
        this._iosDispatchPlayUriPayload(uri, rendererId, formatId, track);
        ok = await this._waitForIOSPlaybackEvidence(rendererId, 3000, generation);
      }
      if (!ok) {
        console.warn('[TCmd] iOS playback not confirmed after retry — leaving state (native events may be absent)');
      }
    })();
  }

  /** Fix D: 5s self-heal if UI says playing but no progress on iOS network renderer */
  private _scheduleGhostPlaybackHeal(uri: string, rendererId: string, formatId: number, track: any): void {
    this._clearGhostPlaybackTimer();
    if (!this._isIOS()) return;

    const scheduledTrackId = track?.id != null ? String(track.id) : null;

    this._ghostPlaybackTimer = setTimeout(async () => {
      this._ghostPlaybackTimer = null;
      const { msSinceLastPlayTrackCompleted } = await import('./QueueController');
      const s = useMaestroStore.getState();

      if (s.playbackState !== 'playing') return;
      if (!s.rendererHasStream) return;
      if (msSinceLastPlayTrackCompleted() < 5000) return;
      if (this._lastPlayUriSuccessAt > 0 && Date.now() - this._lastPlayUriSuccessAt < 2000) return;

      const nowId = s.currentTrack?.id != null ? String(s.currentTrack.id) : null;
      if (scheduledTrackId !== nowId) return;

      if (!s.activeConnectorRenderer || s.activeConnectorRenderer.id !== rendererId) return;
      if ((s.position || 0) > 0) return;

      console.warn('[Watchdog] Ghost playback detected — re-dispatching playUri');
      const url = s.currentStreamUrl;
      if (!url || String(url).trim() === '') {
        console.warn('[Watchdog] currentStreamUrl empty — cannot heal (no URL to re-send)');
        return;
      }
      try {
        await this.playUri(url, rendererId, formatId, track || s.currentTrack, { skipGhostWatchdog: true });
      } catch (e: any) {
        console.error('[Watchdog] Ghost heal playUri failed:', e?.message || e);
      }
    }, 5000);
  }

  async playUri(
    uri: string,
    rendererId: string,
    formatId: number,
    track: any,
    options?: { skipGhostWatchdog?: boolean },
  ) {
    console.log(`[Transport] playUri: track=${track?.id || '?'} renderer=${rendererId || 'local'}`);

    if (!rendererId && !this._audioRef) {
      console.log('[TCmd] playUri BLOCKED: reason=no rendererId and no audioRef');
      return;
    }

    if (this._playUriFails >= 2 && Date.now() - this._playUriLastFail < 10000) {
      console.log('[TCmd] playUri BLOCKED: reason=cooldown after repeated failures');
      console.warn('[TransportCommand.playUri] Cooldown active — renderer failed', this._playUriFails, 'times recently. Waiting 10s before retry.');
      useMaestroStore.getState().setPlaybackState('paused');
      return;
    }
    if (Date.now() - this._playUriLastFail > 15000) {
      this._playUriFails = 0;
    }

    this._clearGhostPlaybackTimer();

    const store = useMaestroStore.getState();
    store.setCurrentTrack(track);
    store.setCurrentStreamUrl(uri);
    store.setCurrentStreamFormatId(formatId);

    // For renderer paths, stay at 'loading' until the renderer confirms PLAYING
    // via TransportState events. This prevents the synthetic position ticker
    // (useRemotePositionTracker) from running before audio actually starts,
    // which caused phantom progress bars that advance without real playback.
    // For local audio (no rendererId), set 'playing' immediately — the browser
    // audio element starts synchronously and doesn't need confirmation.
    if (rendererId) {
      store.setPlaybackState('loading');
    } else {
      store.setPlaybackState('playing');
    }

    this._flushQueue();
    this._lastPlayUriTimestamp = Date.now();

    return this._enqueue('playUri', async () => {
      // Exclusive bit-perfect path: decode in Rust, write PCM directly to USB DAC
      if (!rendererId && await this._shouldUseExclusive()) {
        const storeSnap = useMaestroStore.getState();
        const zone = storeSnap.discovery?.activeZone;
        // Resolve the REAL CoreAudio device name for exclusive_play.
        // The Connector's AudioDevice struct has:
        //   id = real CoreAudio device name (what resolve_device_id matches against)
        //   name = display name (possibly "USB DAC" — NOT what CoreAudio expects)
        // So we must use device.id, not device.name, when calling exclusive_play.
        const isLocalNative = (zone as any)?.type === 'local-native';
        // selectedUsbDac.id is the Connector's AudioDevice.id (= real CoreAudio name)
        const dacName = isLocalNative
          ? (zone as any)?.id || (zone as any)?.name
          : storeSnap.selectedUsbDac?.id || null;
        const dacId = dacName;
        if (dacName && dacId) {
          console.log('[TCmd] playUri → EXCLUSIVE BIT-PERFECT path for USB DAC:', dacName);
          diagnosticLog('info', 'transport', 'exclusive_play_start', { dac: dacName, rendererType: 'local-native' });
          const C = await this._getConnector();
          await C.exclusiveStop().catch(() => {});
          const sr = track?.sampleRate || track?.sample_rate || 44100;
          const bd = track?.bitDepth || track?.bit_depth || 16;
          const sampleRate = sr < 1000 ? sr * 1000 : sr;
          try {
            await C.exclusivePlay(dacName, uri, sampleRate, bd);
          } catch (err: any) {
            const msg = err?.message || String(err);
            console.error('[TCmd] Exclusive play failed:', msg);
            diagnosticLog('error', 'transport', 'exclusive_play_failed', { dac: dacName, error: msg });
            useMaestroStore.getState().setPlaybackState('paused');
            useMaestroStore.getState().setExclusiveError?.(msg);
            throw err;
          }
          this._startExclusivePoller();
          this._lastPlayUriSuccessAt = Date.now();
          useMaestroStore.getState().setRendererHasStream(true);
          this._playUriFails = 0;
          console.log('[TCmd] playUri EXCLUSIVE SUCCESS');
          return;
        }
      }

      await this._matchUsbDacSampleRate(track);

      const doPlay = async () => {
        if (rendererId && this._isTauri()) {
          const C = await this._getConnector();
          await C.playUri(
            rendererId, uri,
            track?.title || '', track?.artist || '',
            track?.album || '', formatId,
            Math.round(track?.duration || 0),
            track?.artworkUrl || track?.artUrl,
          );
        } else if (rendererId && this._isIOS()) {
          this._iosDispatchPlayUriPayload(uri, rendererId, formatId, track);
        } else if (this._audioRef) {
          this._audioRef.src = uri;
          await this._audioRef.play();
        }
      };

      const RENDERER_NOT_FOUND_RETRIES = 3;
      const RENDERER_NOT_FOUND_DELAY_MS = 4000;

      for (let attempt = 0; attempt <= RENDERER_NOT_FOUND_RETRIES; attempt++) {
        try {
          await doPlay();

          this._lastPlayUriSuccessAt = Date.now();
          const postStore = useMaestroStore.getState();
          postStore.setRendererHasStream(true);
          this._playUriFails = 0;

          // Promote to 'playing' after a short delay if the renderer hasn't
          // confirmed PLAYING via TransportState events yet. This handles
          // renderers that don't push transport state callbacks (e.g., some
          // Bluesound devices, old Rygel firmware). TransportState will
          // override this if it fires first.
          if (rendererId && postStore.playbackState === 'loading') {
            setTimeout(() => {
              const s = useMaestroStore.getState();
              if (s.playbackState === 'loading' && s.rendererHasStream) {
                s.setPlaybackState('playing');
              }
            }, 3000);
          }

          if (rendererId && this._isIOS()) {
            const gen = ++this._iosPlayConfirmGen;
            this._iosBackgroundConfirmPlayback(uri, rendererId, formatId, track, gen, options);
          }

          if (this._isIOS() && rendererId && !options?.skipGhostWatchdog) {
            this._scheduleGhostPlaybackHeal(uri, rendererId, formatId, track);
          }
          return;
        } catch (err: any) {
          const msg = String(err?.message || err || '');
          const isNotFound = msg.toLowerCase().includes('not found');

          if (isNotFound && attempt < RENDERER_NOT_FOUND_RETRIES) {
            console.warn(
              `[TCmd] playUri: renderer "${rendererId}" not found — waiting ${RENDERER_NOT_FOUND_DELAY_MS}ms for rediscovery (attempt ${attempt + 1}/${RENDERER_NOT_FOUND_RETRIES})`,
            );
            await new Promise(r => setTimeout(r, RENDERER_NOT_FOUND_DELAY_MS));
            continue;
          }

          console.error('[TCmd] playUri FAILED: renderer=', rendererId, 'error=', msg || String(err));
          diagnosticLog('error', 'transport', `playUri failed: ${msg || String(err)}`, { renderer_id: rendererId, error: msg || String(err) });
          this._playUriFails++;
          this._playUriLastFail = Date.now();
          useMaestroStore.getState().setRendererHasStream(false);
          useMaestroStore.getState().setPlaybackState('paused');
          throw err;
        }
      }
    });
  }

  async stopAllMultizoneZones() {
    if (!this._isMultizoneActive()) return;
    useMaestroStore.getState().setPlaybackState('stopped');
    await this._multizoneStopAll();
  }

  async stopRenderer(rendererId?: string) {
    const rid = rendererId || useMaestroStore.getState().activeConnectorRenderer?.id;
    if (!rid) return;
    const st = useMaestroStore.getState();
    const az = st.discovery?.activeZone as any;
    diagnosticLog(
      'info',
      'transport',
      `Command dispatched: stop`,
      { command: 'stop', target: st.activeConnectorRenderer?.name || rid, rendererType: az?.transport_type || az?.type },
    );
    await _enforceRaatGap();
    if (this._isTauri()) {
      try {
        const C = await this._getConnector();
        await C.stop(rid);
      } catch {}
    } else if (this._isIOS()) {
      this._iosSendCommand(rid, 'stop');
    }
  }

  /**
   * Pause a specific renderer by ID — bypasses the active renderer lookup.
   * Used during zone switch teardown to stop the OLD renderer before
   * activeConnectorRenderer is updated.
   */
  async pauseRenderer(rendererId: string) {
    console.log('[TransportCommand.pauseRenderer]', rendererId, 'isTauri:', this._isTauri(), 'isIOS:', this._isIOS());
    await _enforceRaatGap();
    if (this._isTauri()) {
      try {
        const C = await this._getConnector();
        await C.pause(rendererId);
      } catch {}
    } else if (this._isIOS()) {
      this._iosSendCommand(rendererId, 'pause');
    }
  }

  // === Private: USB DAC sample rate matching ===

  private async _matchUsbDacSampleRate(track: any): Promise<void> {
    if (!this._isTauri()) return;

    const { selectedUsbDac } = useMaestroStore.getState();
    if (!selectedUsbDac) return;

    const { isExclusiveModeEnabled } = await import('../utils/usbDacNames');
    const uid = selectedUsbDac.uid || '';
    if (!isExclusiveModeEnabled(uid)) return;

    const sampleRate = track?.sampleRate || track?.sample_rate;
    if (!sampleRate || typeof sampleRate !== 'number') return;

    const rateHz = sampleRate < 1000 ? sampleRate * 1000 : sampleRate;
    try {
      const C = await this._getConnector();
      const actual = await C.setDeviceSampleRate(selectedUsbDac.id, rateHz);
      console.log(`[TCmd] USB DAC sample rate: requested ${rateHz} Hz, device set to ${actual} Hz`);
    } catch (err) {
      console.warn('[TCmd] Failed to set USB DAC sample rate:', err);
    }
  }

  // === Private: platform detection ===

  private _isTauri(): boolean {
    return detectPlatform().isTauriDesktop;
  }

  /** True ONLY when the Maestro iOS native app bridge is present (NOT Mobile Safari). */
  private _isIOS(): boolean {
    const result = detectPlatform().isIOSNativeApp;
    if (!this._iosCheckLogged) {
      const p = detectPlatform();
      console.log('[TransportCommand] _isIOS:', result, '| platform:', p.deviceName, '| isIOSMobileSafari:', p.isIOSMobileSafari);
      this._iosCheckLogged = true;
    }
    return result;
  }

  // === Private: iOS native bridge (WKWebView) ===

  private _iosPostMessage(action: string, data: Record<string, any>) {
    try {
      const handler = (window as any).webkit?.messageHandlers?.maestroNative;
      if (!handler) {
        console.error('[TransportCommand] iOS bridge not found — cannot send:', action);
        return;
      }
      handler.postMessage({ action, data });
    } catch (err) {
      console.error('[TransportCommand] iOS bridge postMessage failed:', action, err);
    }
  }

  private _iosSendCommand(rendererId: string, command: string, userInitiated = false) {
    this._iosPostMessage('sendCommand', { deviceId: rendererId, command, userInitiated });
  }

  // === Private: mutex queue ===

  private _flushQueue() {
    for (const cmd of this._queue) {
      cmd.reject(new Error('Flushed by playUri'));
    }
    this._queue = [];
  }

  private _enqueue(type: PendingCommand['type'], execute: () => Promise<void>): Promise<void> {
    return new Promise((resolve, reject) => {
      this._queue.push({ type, execute, resolve, reject });
      if (!this._busy) this._drain();
    });
  }

  private async _drain() {
    if (this._busy || this._queue.length === 0) return;
    this._busy = true;
    const cmd = this._queue.shift()!;
    const timeout = cmd.type === 'playUri' ? 25000 : this._commandTimeout;
    {
      const s = useMaestroStore.getState();
      const r = s.activeConnectorRenderer;
      const az = s.discovery?.activeZone as any;
      diagnosticLog(
        'info',
        'transport',
        `Command dispatched: ${cmd.type} to ${r?.name || az?.name || 'local'}`,
        { command: cmd.type, target: r?.name || az?.name || 'local', rendererType: az?.transport_type || az?.type },
      );
    }
    try {
      await _enforceRaatGap();
      const timer = new Promise<never>((_, rej) =>
        setTimeout(() => rej(new Error('Command timeout')), timeout)
      );
      await Promise.race([cmd.execute(), timer]);
      cmd.resolve();
    } catch (err: any) {
      console.error(`[TCmd:queue] ${cmd.type} failed:`, err?.message || err);
      {
        const s = useMaestroStore.getState();
        const r = s.activeConnectorRenderer;
        diagnosticLog(
          'error',
          'transport',
          `Transport error: ${err?.message || err}`,
          {
            renderer: r?.name,
            command: cmd.type,
            error: String(err?.message || err),
          },
        );
      }
      if (cmd.type === 'playUri') {
        cmd.reject(err);
      } else {
        cmd.resolve();
      }
    } finally {
      this._busy = false;
      this._drain();
    }
  }
}

export const TransportCommand = new TransportCommandImpl();

/** Full transport stop — tears down RAAT session (iOS/Tauri native stop); clears stream + stopped state. */
export async function stop(rendererId?: string): Promise<void> {
  const store = useMaestroStore.getState();
  const id = rendererId ?? store.activeConnectorRenderer?.id;
  if (!id) return;
  const stoppedTrack = store.currentTrack?.title || store.currentTrack?.id || '(unknown)';
  console.log('[Transport] stop requested, clearing active URI — track was:', stoppedTrack, 'renderer:', id);
  await TransportCommand.stopRenderer(id);
  store.setRendererHasStream(false);
  store.clearStreamCache();
  store.setPlaybackState('stopped');
}

(window as any).TransportCommand = TransportCommand;
